import {
  type ChangeEvent,
  type ReactElement,
  type ReactNode,
  useId,
} from "react";
import {
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  Stack,
  TextField,
  type TextFieldProps,
  Typography,
} from "@mui/material";

import {
  type Control,
  type FieldValues,
  type Path,
  useController,
} from "react-hook-form";

type CountryPrefix = {
  code: string;
  flag: string;
  label: string;
  prefix: string;
};

const COUNTRY_PREFIXES: CountryPrefix[] = [
  { code: "no", flag: "🇳🇴", label: "Norway", prefix: "+47" },
].sort((a, b) => b.prefix.length - a.prefix.length);

const DEFAULT_PREFIX = "+47";

const splitPhoneNumber = (
  value: string,
): { prefix: string; number: string } => {
  const trimmed = value.trim();

  const matched = COUNTRY_PREFIXES.find((p) => trimmed.startsWith(p.prefix));

  if (matched) {
    return {
      prefix: matched.prefix,
      number: trimmed.slice(matched.prefix.length),
    };
  }

  return { prefix: DEFAULT_PREFIX, number: trimmed };
};

const joinPhoneNumber = (prefix: string, number: string): string =>
  `${prefix}${number.trim()}`;

type PhoneInputProps<T extends FieldValues> = {
  fieldName: Path<T>;
  label?: ReactNode;
  hideLabel?: boolean;
  hideError?: boolean;
  control: Control<T>;
  fullWidth?: boolean;
  hide?: boolean;
} & TextFieldProps;

export const PhoneInput = <T extends FieldValues>({
  fieldName,
  label,
  hideLabel = false,
  hideError = false,
  fullWidth = true,
  control,
  hide,
  ...inputProps
}: PhoneInputProps<T>): ReactElement | null => {
  const id = useId();
  const prefixId = useId();

  const {
    field: { value, onChange, onBlur, ref, name },
    fieldState: { error },
  } = useController({
    name: fieldName,
    control,
  });

  if (hide) {
    return null;
  }

  const { prefix, number } = splitPhoneNumber(value ?? "");
  const hasKnownPrefix = COUNTRY_PREFIXES.some((p) => p.prefix === prefix);

  return (
    <>
      {!hideLabel && <Typography id={id}>{label}</Typography>}
      <FormControl fullWidth={fullWidth} error={!!error} size={"small"}>
        <Stack direction={"row"} sx={{ gap: 1 }}>
          <Select
            value={prefix}
            onChange={(e) => onChange(joinPhoneNumber(e.target.value, number))}
            size={"small"}
            disabled={inputProps.disabled}
            error={!!error}
            sx={{ minWidth: "fit-content" }}
            slotProps={{
              input: {
                "aria-label": "Country prefix",
                id: prefixId,
              },
            }}
          >
            {!hasKnownPrefix && prefix && (
              <MenuItem value={prefix}>{prefix}</MenuItem>
            )}
            {COUNTRY_PREFIXES.map((p) => (
              <MenuItem key={p.code} value={p.prefix}>
                {p.flag} {p.prefix}
              </MenuItem>
            ))}
          </Select>
          <TextField
            type={"tel"}
            size={"small"}
            fullWidth
            {...inputProps}
            name={name}
            inputRef={ref}
            value={number}
            error={!!error}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onChange(joinPhoneNumber(prefix, e.target.value))
            }
            onBlur={onBlur}
            slotProps={{
              htmlInput: {
                "aria-labelledby": hideLabel ? undefined : id,
                "aria-label": hideLabel ? label : undefined,
              },
            }}
          />
        </Stack>
        {!hideError && error?.message && (
          <FormHelperText>{error.message}</FormHelperText>
        )}
      </FormControl>
    </>
  );
};
